/**
 * Service để gửi dữ liệu đặt lịch và đơn hàng đến CRM API
 * Sử dụng backend proxy để tránh lỗi CORS
 */

import { API_HEAD } from '@/lib/apiConfig';

/**
 * Lấy CRM proxy URL - sử dụng API_HEAD từ config
 */
function getCrmProxyUrl(): string {
  return `${API_HEAD}crm-proxy/data-external`;
}

// Note: Frontend không cần token, backend sẽ tự động thêm CRM_TOKEN từ env khi forward đến CRM

export interface BookingFormData {
  fullName: string;
  phone: string;
  email: string;
  gender?: string;
  occupation?: string;
  dateOfBirth?: string;
  service: string;
  note?: string;
}

export interface OrderFormData {
  fullName: string;
  phone: string;
  email: string;
  serviceId: number;
  serviceTitle?: string;
  paymentMethod: 'MOMO' | 'VNPAY';
  paymentAmount?: number;
  gender?: string;
  occupation?: string;
  dateOfBirth?: string;
  note?: string;
}

interface CrmApiPayload {
  desc?: string;
  extra_desc?: string;
  phone?: string;
  gender?: "Nam" | "Nữ";
  services: string[];
  fullname?: string;
  occupation?: string;
  dateOfBirth?: string;
}

class CrmApiService {
  /**
   * Chuyển đổi dữ liệu từ form sang format của CRM API
   */
  private transformToCrmFormat(data: BookingFormData): CrmApiPayload {
    // Chuẩn bị extra_desc với các trường không có trong format chính
    const extraData: Record<string, any> = {};
    if (data.email) extraData.email = data.email;

    // Chuyển đổi dateOfBirth sang ISO string nếu có
    let dateOfBirthISO: string | undefined;
    if (data.dateOfBirth) {
      try {
        const date = new Date(data.dateOfBirth);
        dateOfBirthISO = date.toISOString();
      } catch (e) {
        // Nếu không parse được, giữ nguyên
        dateOfBirthISO = data.dateOfBirth;
      }
    }

    // Giới hạn fullname max 50 ký tự
    const fullname = data.fullName ? data.fullName.substring(0, 50) : undefined;

    // Chuyển service thành array (bắt buộc)
    const services = data.service ? [data.service] : [];

    // Chỉ giữ gender nếu là "Nam" hoặc "Nữ"
    let gender: "Nam" | "Nữ" | undefined;
    if (data.gender === "Nam" || data.gender === "Nữ") {
      gender = data.gender;
    }

    const payload: CrmApiPayload = {
      services: services, // Bắt buộc
    };

    // Chỉ thêm các trường nếu có giá trị
    if (data.note) payload.desc = data.note;
    if (Object.keys(extraData).length > 0) {
      payload.extra_desc = JSON.stringify(extraData);
    }
    if (data.phone) payload.phone = data.phone;
    if (gender) payload.gender = gender;
    if (fullname) payload.fullname = fullname;
    if (data.occupation) payload.occupation = data.occupation;
    if (dateOfBirthISO) payload.dateOfBirth = dateOfBirthISO;

    return payload;
  }

  /**
   * Chuyển đổi dữ liệu đơn hàng sang format của CRM API
   */
  private transformOrderToCrmFormat(data: OrderFormData): CrmApiPayload {
    // Chuẩn bị extra_desc với các trường không có trong format chính
    const extraData: Record<string, any> = {};
    if (data.email) extraData.email = data.email;
    if (data.serviceId) extraData.serviceId = data.serviceId;
    if (data.paymentMethod) extraData.paymentMethod = data.paymentMethod;
    if (data.paymentAmount) extraData.paymentAmount = data.paymentAmount;

    // Chuyển đổi dateOfBirth sang ISO string nếu có
    let dateOfBirthISO: string | undefined;
    if (data.dateOfBirth) {
      try {
        const date = new Date(data.dateOfBirth);
        dateOfBirthISO = date.toISOString();
      } catch (e) {
        // Nếu không parse được, giữ nguyên
        dateOfBirthISO = data.dateOfBirth;
      }
    }

    // Giới hạn fullname max 50 ký tự
    const fullname = data.fullName ? data.fullName.substring(0, 50) : undefined;

    // Chuyển service thành array (bắt buộc) - ưu tiên serviceTitle, nếu không có thì dùng serviceId
    const services = data.serviceTitle ? [data.serviceTitle] : data.serviceId ? [`Service ID: ${data.serviceId}`] : [];

    // Chỉ giữ gender nếu là "Nam" hoặc "Nữ"
    let gender: "Nam" | "Nữ" | undefined;
    if (data.gender === "Nam" || data.gender === "Nữ") {
      gender = data.gender;
    }

    const payload: CrmApiPayload = {
      services: services, // Bắt buộc
    };

    // Chỉ thêm các trường nếu có giá trị
    if (data.note) payload.desc = data.note;
    if (Object.keys(extraData).length > 0) {
      payload.extra_desc = JSON.stringify(extraData);
    }
    if (data.phone) payload.phone = data.phone;
    if (gender) payload.gender = gender;
    if (fullname) payload.fullname = fullname;
    if (data.occupation) payload.occupation = data.occupation;
    if (dateOfBirthISO) payload.dateOfBirth = dateOfBirthISO;

    return payload;
  }

  /**
   * Gửi dữ liệu đặt lịch đến CRM API qua backend proxy
   */
  async sendBooking(data: BookingFormData): Promise<{ success: boolean; message?: string; error?: any }> {
    try {
      // Transform dữ liệu
      const payload = this.transformToCrmFormat(data);

      // Validate payload
      if (!payload.services || payload.services.length === 0) {
        console.error('❌ CRM API: Services array is empty', { data, payload });
        return {
          success: false,
          message: 'Dịch vụ không được để trống',
        };
      }

      const crmProxyUrl = getCrmProxyUrl();

      // Log thông tin để debug (chỉ trong development)
      const isDevelopment = process.env.NODE_ENV === 'development';
      if (isDevelopment) {
        console.log('📤 Sending to CRM API:', {
          url: crmProxyUrl,
          apiHead: API_HEAD,
          payload,
          windowOrigin: typeof window !== 'undefined' ? window.location.origin : 'server-side',
          note: 'Backend sẽ tự động thêm CRM_TOKEN từ env',
        });
      }

      // Frontend chỉ gửi dữ liệu, backend sẽ tự động thêm token và forward đến CRM
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      // Gửi request qua backend proxy để tránh lỗi CORS
      const response = await fetch(crmProxyUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      }).catch((fetchError) => {
        // Network error hoặc CORS error
        console.error('❌ CRM API fetch error:', {
          url: crmProxyUrl,
          apiHead: API_HEAD,
          error: fetchError.message,
          errorType: fetchError.name,
        });
        throw new Error(`Không thể kết nối đến server: ${fetchError.message}`);
      });

      // Parse response
      let responseData: any = {};
      try {
        const text = await response.text();
        if (text) {
          responseData = JSON.parse(text);
        }
      } catch (parseError) {
        console.error('❌ CRM API response parse error:', {
          status: response.status,
          statusText: response.statusText,
        });
        throw new Error(`Lỗi parse response từ server: ${response.status} ${response.statusText}`);
      }
      
      // Kiểm tra cả status code và error trong response data
      if (!response.ok || responseData.error || (responseData.statusCode && responseData.statusCode !== 200)) {
        console.error('❌ CRM API error:', {
          url: crmProxyUrl,
          apiHead: API_HEAD,
          status: response.status,
          statusText: response.statusText,
          data: responseData,
        });
        throw new Error(responseData.message || responseData.error || `CRM API error: ${response.status}`);
      }
      
      return {
        success: true,
        message: 'Data sent to CRM API successfully',
      };
    } catch (error: any) {
      const crmProxyUrl = getCrmProxyUrl();
      console.error('❌ CRM API error:', {
        url: crmProxyUrl,
        apiHead: API_HEAD,
        message: error.message,
        error: error,
      });
      return {
        success: false,
        message: 'Failed to send data to CRM API',
        error: error.message,
      };
    }
  }

  /**
   * Gửi dữ liệu đơn hàng đến CRM API qua backend proxy
   */
  async sendOrder(data: OrderFormData): Promise<{ success: boolean; message?: string; error?: any }> {
    try {
      // Transform dữ liệu
      const payload = this.transformOrderToCrmFormat(data);

      // Validate payload
      if (!payload.services || payload.services.length === 0) {
        console.error('❌ CRM API: Services array is empty', { data, payload });
        return {
          success: false,
          message: 'Dịch vụ không được để trống',
        };
      }

      const crmProxyUrl = getCrmProxyUrl();

      // Log thông tin để debug (chỉ trong development hoặc khi có lỗi)
      const isDevelopment = process.env.NODE_ENV === 'development';
      if (isDevelopment) {
        console.log('📤 Sending order to CRM API:', {
          url: crmProxyUrl,
          apiHead: API_HEAD,
          payload,
          windowOrigin: typeof window !== 'undefined' ? window.location.origin : 'server-side',
          note: 'Backend sẽ tự động thêm CRM_TOKEN từ env',
        });
      }

      // Frontend chỉ gửi dữ liệu, backend sẽ tự động thêm token và forward đến CRM
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      // Gửi request qua backend proxy để tránh lỗi CORS
      const response = await fetch(crmProxyUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      }).catch((fetchError) => {
        // Network error hoặc CORS error
        console.error('❌ CRM API fetch error:', {
          url: crmProxyUrl,
          apiHead: API_HEAD,
          error: fetchError.message,
          errorType: fetchError.name,
        });
        throw new Error(`Không thể kết nối đến server: ${fetchError.message}`);
      });

      // Parse response
      let responseData: any = {};
      try {
        const text = await response.text();
        if (text) {
          responseData = JSON.parse(text);
        }
      } catch (parseError) {
        console.error('❌ CRM API response parse error:', {
          status: response.status,
          statusText: response.statusText,
        });
        throw new Error(`Lỗi parse response từ server: ${response.status} ${response.statusText}`);
      }
      
      // Kiểm tra cả status code và error trong response data
      if (!response.ok || responseData.error || (responseData.statusCode && responseData.statusCode !== 200)) {
        console.error('❌ CRM API error:', {
          url: crmProxyUrl,
          apiHead: API_HEAD,
          status: response.status,
          statusText: response.statusText,
          data: responseData,
        });
        throw new Error(responseData.message || responseData.error || `CRM API error: ${response.status}`);
      }
      
      return {
        success: true,
        message: 'Order data sent to CRM API successfully',
      };
    } catch (error: any) {
      const crmProxyUrl = getCrmProxyUrl();
      console.error('❌ CRM API error:', {
        url: crmProxyUrl,
        apiHead: API_HEAD,
        message: error.message,
        error: error,
      });
      return {
        success: false,
        message: 'Failed to send order data to CRM API',
        error: error.message,
      };
    }
  }
}

const crmApiService = new CrmApiService();
export default crmApiService;

